<template>
  <BaseModal :is-open="isOpen" fullscreen size="xl" @close="close">
    <template #header>
      <div class="flex justify-between px-4 pt-6 sm:px-8 sm:pt-8">
        <div class="text-2xl font-bold w-3/4">
          {{ $t('tickets.activation_modal_title') }}
        </div>
        <BaseButton theme="white" size="sm" class="w-8 !p-0" @click="close">
          <BaseIcon name="outline_x" />
        </BaseButton>
      </div>
    </template>
    <template #body>
      <div class="px-4 py-6 sm:px-8 sm:pb-10">
        <BaseForm
          ref="formComponent"
          class="w-full flex flex-col gap-6"
          :reset-on-submit="false"
          :method="submit"
        >
          <div v-if="!isSubscription">
            <div class="font-bold text-lg pb-2">
              {{ $t('tickets.activation_modal_subtitle') }}
            </div>
            <BaseRadioGroup
              v-model="ticketOwner"
              :options="ticketOwnerOptions"
              :is-column-view="false"
              class="pb-2"
              @input="onChangeOwner"
            />
          </div>
          <BaseInput
            v-model="form.email"
            name="email"
            type="email"
            clear-input
            rules="email|required"
            :placeholder="$t('profile.email')"
            :label="$t('profile.email')"
            required
            :disabled="ticketOwner === TicketOwner.Me"
            @change="trimWhitespace('email')"
          />
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <BaseInput
              v-model="form.first_name"
              name="first_name"
              rules="name|required"
              :placeholder="$t('profile.first_name')"
              :label="$t('profile.first_name')"
              required
              @change="trimWhitespace('first_name')"
            />
            <BaseInput
              v-model="form.last_name"
              name="last_name"
              rules="name|required"
              :placeholder="$t('profile.last_name')"
              :label="$t('profile.last_name')"
              required
              @change="trimWhitespace('last_name')"
            />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <BaseSelectAsync
              ref="countrySelector"
              v-model="form.country_id"
              :placeholder="$t('profile.select_country')"
              :label="$t('profile.country')"
              name="country_id"
              required
              :initial="initialCountry"
              :fetch="getCountriesAutocomplete"
              @change="onCountryChange"
            />
            <BaseSelectAsync
              ref="citySelector"
              v-model="form.city_id"
              :label="$t('profile.city')"
              :placeholder="$t('profile.select_city')"
              name="city_id"
              required
              :initial="initialCity"
              :fetch="fetchCitiesAutocomplete"
            />
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <BaseInput
              v-model="form.job"
              name="job"
              rules="required"
              :placeholder="$t('profile.job')"
              :label="$t('profile.job')"
              required
            />
            <BaseSelectAsync
              ref="companySelector"
              v-model="form.company_id"
              :placeholder="$t('profile.select_or_create_company')"
              :search-placeholder="$t('profile.search_or_create_company')"
              :label="$t('profile.company')"
              name="company_id"
              required
              reset-on-open
              :fetch-on-open="false"
              :initial="initialCompany"
              :fetch="companiesStore.getCompaniesOptions"
              @change="onCompanyChange"
            >
              <template #option="{ option }">
                <span class="flex-1 truncate" :title="option.label">
                  {{ option.label }}
                  <span
                    v-if="option.label === option.value"
                    class="text-sm text-gray-400"
                  >
                    {{ $t('profile.new_company') }}
                  </span>
                </span>
              </template>
            </BaseSelectAsync>
          </div>
          <template v-if="isCreateNewCompany">
            <BaseMultiSelect
              v-model="newCompanyForm.business"
              name="business"
              :label="$t('companies.company_business')"
              :placeholder="$t('companies.select_company_business')"
              :required="isCreateNewCompany"
              :options="dictStore.company.business"
            >
              <template #option="{ value, label, removeOption }">
                <BaseButton
                  class="pr-2 pl-3 m-1"
                  size="sm"
                  rounded="full"
                  @click="() => removeOption(value)"
                >
                  <span>{{ label }}</span>
                  <BaseIcon name="outline_x" class="ml-2" size="xs" />
                </BaseButton>
              </template>
            </BaseMultiSelect>
          </template>
          <BaseButton
            type="submit"
            class="mt-2 ml-auto"
            :loading="isProcessing"
            :disabled="isProcessing"
          >
            {{ $t('common.activate') }}
          </BaseButton>
        </BaseForm>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useSubscriptions } from '~/stores/subscriptions'
import { useNotifications } from '~/stores/notifications'
import { useDictionaries } from '~/stores/dictionaries'
import type { UserProfile } from '~/models/users'
import type { OptionInterface } from '~/components/base/controls/use-simple-input'
import BaseSelectAsync from '~/components/base/controls/BaseSelectAsync.vue'
import { toAutocomplete } from '~/utils/autocomplete'
import type { CompanyCreate } from '~/models/companies'
import { useAuth } from '~/stores/auth'
import { useCompanies } from '~/stores/companies'
import { useOrders } from '~/stores/orders'
import { useEvents } from '~/stores/events'
import BaseForm from '~/components/base/BaseForm.vue'

interface PropsInterface {
  isOpen?: boolean
  ticketId: number
  isSubscription?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isOpen: false,
  isSubscription: false,
})

const emit = defineEmits(['close', 'activated'])

const { t } = useI18n()
const authStore = useAuth()
const { activateSubscriptionTicket } = useSubscriptions()
const { pushNotification } = useNotifications()
const { getCitiesAutocomplete, getCountriesAutocomplete, detectGeo } = useGeo()
const companiesStore = useCompanies()
const dictStore = useDictionaries()
const { fetchDictionaries } = dictStore
const { activateOrderItem, fetchItemsToActivateCount } = useOrders()
const { resetEvent } = useEvents()

const isProcessing = ref(false)
const isCreateNewCompany = ref(false)
const formComponent = ref<InstanceType<typeof BaseForm> | null>(null)

const citySelector = ref<InstanceType<typeof BaseSelectAsync> | null>(null)
const countrySelector = ref<InstanceType<typeof BaseSelectAsync> | null>(null)
const companySelector = ref<InstanceType<typeof BaseSelectAsync> | null>(null)

const form = reactive<Partial<UserProfile>>({
  first_name: '',
  last_name: '',
  country_id: null,
  city_id: null,
  email: '',
  company_id: null,
  job: '',
})

const setFormData = () => {
  form.first_name = authStore.profile?.first_name
  form.last_name = authStore.profile?.last_name
  form.email = authStore.profile?.email
  form.job = authStore.profile?.job
}

const resetFormData = () => {
  form.first_name = ''
  form.last_name = ''
  form.job = ''
  form.email = ''
}

const newCompanyForm = reactive<CompanyCreate>({
  name: '',
  geo: null as string | null,
  business: [] as number[],
})

enum TicketOwner {
  Me,
  Another,
}

const ticketOwner = ref<TicketOwner>(TicketOwner.Me)

const ticketOwnerOptions = [
  { value: TicketOwner.Me, label: t('tickets.ticket_owner_me') },
  { value: TicketOwner.Another, label: t('tickets.ticket_owner_another') },
]

const onChangeOwner = () => {
  if (ticketOwner.value === TicketOwner.Another) {
    resetFormData()
  } else {
    setFormData()
  }
}

const close = () => {
  emit('close')
}

const initialCountry = ref<OptionInterface | undefined>(undefined)
const initialCity = ref<OptionInterface | undefined>(undefined)
const initialCompany = ref<OptionInterface | undefined>(undefined)

function fillInitialData() {
  if (
    authStore.profile?.country &&
    typeof authStore.profile.country === 'object'
  ) {
    initialCountry.value = toAutocomplete(authStore.profile.country)
    form.country_id = authStore.profile.country.id
  } else {
    initialCountry.value = undefined
    form.country_id = null
  }

  if (authStore.profile?.city && typeof authStore.profile.city === 'object') {
    initialCity.value = toAutocomplete(authStore.profile.city)
    form.city_id = authStore.profile.city.id
  } else {
    initialCity.value = undefined
    form.city_id = null
  }

  if (
    authStore.profile?.company &&
    typeof authStore.profile.company === 'object'
  ) {
    initialCompany.value = toAutocomplete(authStore.profile.company)
    form.company_id = authStore.profile.company_id
  } else {
    initialCompany.value = undefined
    form.company_id = null
  }
}

fillInitialData()

const fetchCitiesAutocomplete = (query: string) => {
  return form.country_id
    ? getCitiesAutocomplete(String(form.country_id), query)
    : []
}

const onCountryChange = () => {
  const cityInput = citySelector.value

  form.city_id = null
  initialCity.value = undefined
  cityInput?.clear()
  cityInput?.clearOptions()
}

const onCompanyChange = (option: OptionInterface) => {
  isCreateNewCompany.value = option.value === option.label
  newCompanyForm.name = option.value as string
}

onBeforeMount(() => {
  setFormData()
})

const detectedGeo = ref<{
  country: OptionInterface | null
  city: OptionInterface | null
}>({ city: null, country: null })

function fillDetectedGeo() {
  if (!form.country_id) {
    if (
      (!form.city_id || form.city_id === detectedGeo.value.city?.value) &&
      detectedGeo.value.country?.value
    ) {
      form.country_id = String(detectedGeo.value.country?.value)
      initialCountry.value = detectedGeo.value.country
    }
  }

  if (!form.city_id) {
    if (
      (!form.country_id ||
        form.country_id === detectedGeo.value.country?.value) &&
      detectedGeo.value.city?.value
    ) {
      form.city_id = detectedGeo.value.city?.value as number
      initialCity.value = detectedGeo.value.city
    }
  }
}

const trimWhitespace = (field: keyof typeof form) => {
  form[field] = form[field]?.trim() || ''
}

onMounted(async () => {
  fetchDictionaries()

  if (form.country_id && form.city_id) {
    return
  }

  detectedGeo.value = await detectGeo()

  fillDetectedGeo()
})

onUnmounted(() => {
  formComponent.value?.resetForm()
})

const submit = async () => {
  isProcessing.value = true

  if (isCreateNewCompany.value) {
    newCompanyForm.geo = form.country_id

    try {
      await companiesStore.createCompany(newCompanyForm)

      form.company_id = companiesStore.company?.id

      isCreateNewCompany.value = false
    } catch (error: any) {
      pushNotification({
        title: t('notifications.companies.create_error'),
        theme: 'destructive',
      })

      isProcessing.value = false
      useLogError(error)
      return
    }
  }

  try {
    props.isSubscription
      ? await activateSubscriptionTicket(props.ticketId, form)
      : await activateOrderItem(props.ticketId, form)
    await authStore.refreshProfile()
    close()
    pushNotification({
      title: t('notifications.tickets.activate_ticket_success'),
    })
    emit('activated')
    await fetchItemsToActivateCount()

    // we reset event to get actual info if user goes to event page after activation
    resetEvent()
  } catch (error: any) {
    if (error.statusCode === 422 && typeof error.data.message === 'string') {
      pushNotification({
        title: t('notifications.tickets.activate_ticket_error'),
        description: error.data.message,
        theme: 'destructive',
      })
    } else {
      pushNotification({
        title: t('notifications.tickets.activate_ticket_error'),
        theme: 'destructive',
      })
    }
    useLogError(error)
  } finally {
    isProcessing.value = false
  }
}
</script>
